import {
  CheckCircle2,
  FileText,
  HelpCircle,
  Mail,
  Users as MeetingIcon,
  MessageSquare,
  Phone,
  Plus,
  Send,
  StickyNote,
} from 'lucide-react';
import { useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import {
  COMMUNICATION_STATUS_LABELS,
  COMMUNICATION_TYPE_LABELS,
  type CommunicationType,
  useCommunications,
  useCreateCommunication,
  useReplyCommunication,
  useResolveCommunication,
} from '@/hooks/use-communications';

const TYPE_ICONS: Record<CommunicationType, any> = {
  EMAIL: Mail,
  PHONE_CALL: Phone,
  MEETING: MeetingIcon,
  LETTER: FileText,
  INTERNAL_NOTE: StickyNote,
  ENQUIRY: HelpCircle,
};

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Props {
  loanId: string;
}

export function CommunicationsTab({ loanId }: Props) {
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const { data: comms, isLoading } = useCommunications(loanId, {
    type: typeFilter || undefined,
    status: statusFilter || undefined,
  });
  const createComm = useCreateCommunication(loanId);
  const replyComm = useReplyCommunication(loanId);
  const resolveComm = useResolveCommunication(loanId);
  const [showCreate, setShowCreate] = useState(false);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');

  const [form, setForm] = useState({
    type: 'EMAIL' as CommunicationType,
    direction: 'OUTBOUND',
    subject: '',
    body: '',
    contactName: '',
    fromEmail: '',
    toEmail: '',
    priority: 'NORMAL',
  });

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    createComm.mutate(form, {
      onSuccess: () => {
        setShowCreate(false);
        setForm({ ...form, subject: '', body: '', contactName: '', fromEmail: '', toEmail: '' });
      },
    });
  }

  function handleReply(cid: string) {
    if (!replyText.trim()) return;
    replyComm.mutate(
      {
        cid,
        body: replyText,
        direction: 'OUTBOUND',
        subject: 'RE: ' + (comms?.find(c => c.id === cid)?.subject || ''),
      },
      {
        onSuccess: () => {
          setReplyTo(null);
          setReplyText('');
        },
      },
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-900">Communications</h3>
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            className="text-xs border border-gray-300 rounded px-2 py-1"
          >
            <option value="">All Types</option>
            {Object.entries(COMMUNICATION_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="text-xs border border-gray-300 rounded px-2 py-1"
          >
            <option value="">All Statuses</option>
            {Object.entries(COMMUNICATION_STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowCreate(!showCreate)}
          className="w-fit flex items-center gap-1.5 px-3 py-1.5 text-white text-sm rounded-md hover:"
        >
          <Plus className="w-3.5 h-3.5" /> Log Communication
        </Button>
      </div>

      {/* Create form */}
      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Type *</label>
              <select
                value={form.type}
                onChange={e => setForm({ ...form, type: e.target.value as CommunicationType })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              >
                {Object.entries(COMMUNICATION_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Direction</label>
              <select
                value={form.direction}
                onChange={e => setForm({ ...form, direction: e.target.value })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              >
                <option value="OUTBOUND">Outbound</option>
                <option value="INBOUND">Inbound</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Priority</label>
              <select
                value={form.priority}
                onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              >
                <option value="LOW">Low</option>
                <option value="NORMAL">Normal</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Contact Name</label>
              <input
                value={form.contactName}
                onChange={e => setForm({ ...form, contactName: e.target.value })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Subject *</label>
            <input
              value={form.subject}
              onChange={e => setForm({ ...form, subject: e.target.value })}
              required
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              placeholder="Subject line or call summary"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Body / Notes</label>
            <textarea
              rows={3}
              value={form.body}
              onChange={e => setForm({ ...form, body: e.target.value })}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm resize-none"
              placeholder="Details of the communication..."
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="primary"
              type="submit"
              disabled={createComm.isPending}
              className="w-fit px-3 py-1.5 text-white text-sm rounded hover: disabled:opacity-50"
            >
              {createComm.isPending ? 'Creating…' : 'Log Communication'}
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={() => setShowCreate(false)}
              className="w-fit"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Communications list */}
      {comms && comms.length > 0 ? (
        <div className="space-y-2">
          {comms.map(c => {
            const Icon = TYPE_ICONS[c.type] || MessageSquare;
            const statusCfg = COMMUNICATION_STATUS_LABELS[c.status];
            return (
              <div key={c.id} className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-mono text-indigo-600 font-medium">
                      {c.referenceNo}
                    </span>
                    <span
                      className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full`}
                      style={{ backgroundColor: statusCfg.bg, color: statusCfg.color }}
                    >
                      {statusCfg.label}
                    </span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full ${c.direction === 'INBOUND' ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600'}`}
                    >
                      {c.direction === 'INBOUND' ? '← In' : '→ Out'}
                    </span>
                    {c.priority === 'HIGH' && (
                      <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
                        HIGH
                      </span>
                    )}
                    {c.priority === 'URGENT' && (
                      <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">
                        URGENT
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-400">{fmtDate(c.createdAt)}</span>
                    {c.status !== 'RESOLVED' && c.status !== 'CLOSED' && (
                      <button
                        onClick={() => resolveComm.mutate(c.id)}
                        className="text-[10px] text-green-600 hover:text-green-700"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                <p className="text-sm font-medium text-gray-900 mb-1">{c.subject}</p>
                {c.body && <p className="text-xs text-gray-600 mb-2 line-clamp-2">{c.body}</p>}

                <div className="flex items-center gap-3 text-[10px] text-gray-400">
                  {c.contactName && <span>Contact: {c.contactName}</span>}
                  {c.createdBy && (
                    <span>
                      By: {c.createdBy.firstName} {c.createdBy.lastName}
                    </span>
                  )}
                </div>

                {/* Reply */}
                {replyTo === c.id ? (
                  <div className="mt-2 flex gap-2">
                    <input
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      placeholder="Type reply…"
                      className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                    <Button
                      variant="primary"
                      onClick={() => handleReply(c.id)}
                      disabled={replyComm.isPending}
                      className="px-2 py-1.5 text-white text-xs rounded hover:"
                    >
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                    <button
                      onClick={() => setReplyTo(null)}
                      className="px-2 py-1.5 border border-gray-300 text-xs rounded"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setReplyTo(c.id)}
                    className="mt-2 text-[10px] text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Reply →
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-10 text-gray-400">
          <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No communications logged yet</p>
        </div>
      )}
    </div>
  );
}
